import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { EmptyState, Icons } from '@dbrain/ui';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate, type Location } from 'react-router';
import { isApiError, whenLicenseRefuses, whenSessionEnds } from './api/client.js';
import { keys, useMe } from './api/hooks.js';
import { licenseKey } from './api/license.js';
import { GuestOnly, RequireAuth, RequireCapability } from './auth/guards.js';
import { AppLayout } from './components/AppLayout.js';
import { CookieNotice } from './components/CookieNotice.js';
import { AppError, ErrorBoundary } from './components/ErrorBoundary.js';
import type { BackgroundState } from './components/settings/links.js';
import { SettingsDialog } from './components/settings/SettingsDialog.js';
import { setLocale } from './i18n/index.js';
import { ConnectorsPage } from './pages/ConnectorsPage.js';
import { ConversationPage } from './pages/ConversationPage.js';
import { DocumentsPage } from './pages/DocumentsPage.js';
import { HistoryPage } from './pages/HistoryPage.js';
import { HomePage } from './pages/HomePage.js';
import { SkillEditorPage } from './pages/SkillEditorPage.js';
import { SkillsPage } from './pages/SkillsPage.js';
import { WorkspacesPage } from './pages/WorkspacesPage.js';
import { AccessPage } from './pages/admin/AccessPage.js';
import { AdminHome } from './pages/admin/AdminLayout.js';
import { AuditPage } from './pages/admin/AuditPage.js';
import { IdentityPage } from './pages/admin/IdentityPage.js';
import { LicensePage } from './pages/admin/LicensePage.js';
import { SkillAnalyticsPage } from './pages/admin/SkillAnalyticsPage.js';
import { IntegrationsPage, Microsoft365Page, Microsoft365SetupPage } from './pages/admin/IntegrationsPage.js';
import { ModelsAdminPage } from './pages/admin/ModelsAdminPage.js';
import { PoliciesPage } from './pages/admin/PoliciesPage.js';
import { UsagePage } from './pages/admin/UsagePage.js';
import { PlatformPage } from './pages/platform/PlatformPage.js';
import { useCapabilities, useMeta } from './experience.js';
import { canonicalAddress } from '@dbrain/app-core/origins';
import { BreakGlassLoginPage, SignInPage } from './pages/SignInPage.js';
import { WorkspacePage } from './pages/WorkspacePage.js';
import { CheckEmailPage, InvitePage, VerifyEmailPage } from './pages/auth.js';
import { CheckoutPage } from './pages/bireysel/CheckoutPage.js';
import { LegalDraftPage } from './pages/bireysel/LegalDraftPage.js';
import { LandingPage, PricingPage } from './pages/bireysel/PublicSite.js';
import { UnsubscribePage } from './pages/bireysel/UnsubscribePage.js';
import { PersonalForgotPasswordPage, PersonalResetPasswordPage, PersonalSignupPage, PersonalVerifyPage } from './pages/bireysel/PersonalAuth.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => count < 2 && (isApiError(error, 'network_error') || isApiError(error, 'service_unavailable')),
      refetchOnWindowFocus: false,
    },
  },
});

// a request that finds the session over takes the app back to the sign-in page (RequireAuth sees no user)
whenSessionEnds(() => {
  if (queryClient.getQueryData(keys.me)) queryClient.setQueryData(keys.me, null);
});

// a write refused by the license: the chip, the read-only card or the product's page show at once
whenLicenseRefuses(() => void queryClient.invalidateQueries({ queryKey: licenseKey }));

/** Applies the signed-in user's saved language once it is known. */
function LocaleSync() {
  const me = useMe();
  const locale = me.data?.user.locale;
  useEffect(() => {
    if (locale) void setLocale(locale);
  }, [locale]);
  return null;
}

function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="page page--center" data-testid="not-found">
      <EmptyState icon={<Icons.search />} title={t('notFound.title')} action={<Link className="ui-button ui-button--secondary" to="/">{t('notFound.back')}</Link>}>
        {t('notFound.body')}
      </EmptyState>
    </div>
  );
}

/**
 * Every signed-in page is a `Protected` element, so moving between them keeps the shell (sidebar, menus, focus) and
 * swaps only the page. Phase 16c (B2cTanitim): `guest` is what a visitor sees instead of the sign-in page (the
 * bireysel landing page at `/`).
 */
function Protected({ children, guest }: { children: ReactNode; guest?: ReactNode }) {
  return (
    <RequireAuth guest={guest}>
      <AppLayout>{children}</AppLayout>
    </RequireAuth>
  );
}

/**
 * The settings dialog opens over the page it was called from; opened by
 * its address, the home page is behind it (decision of Phase 13b).
 */
/** D-100: the admin sections that lived in the settings dialog are pages of Yönetim now. */
const MOVED_SETTINGS: Record<string, string> = {
  limits: '/admin/usage/members',
  audit: '/admin/audit',
  policies: '/admin/policies',
  email: '/admin/policies',
  identity: '/admin/identity',
};

function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const me = useMe();
  const capabilities = useCapabilities();
  // D-153: opened at the server's old IP address, the browser goes on to the https address
  const meta = useMeta().data;
  useEffect(() => {
    if (import.meta.env.DEV) return;
    // Phase 16a: an edition without Code has one address; its old code address opens the chat's
    const here = window.location;
    const chatOnly = meta?.capabilities && !meta.capabilities.codeExperience && meta.chatOrigin !== here.origin ? `${meta.chatOrigin}${here.pathname}${here.search}${here.hash}` : null;
    const to = chatOnly ?? canonicalAddress(meta, here);
    if (to) window.location.replace(to);
  }, [meta]);
  const moved = location.pathname.startsWith('/settings/') ? MOVED_SETTINGS[location.pathname.split('/')[2] ?? ''] : undefined;
  if (moved) return <Navigate to={moved} replace />;
  const settings = location.pathname === '/settings' || location.pathname.startsWith('/settings/');
  const background: Location | null = settings ? ((location.state as BackgroundState | null)?.background ?? { ...location, pathname: '/', search: '', hash: '', state: null }) : null;
  const closeSettings = () => void navigate(background ? `${background.pathname}${background.search}` : '/', { replace: true });
  return (
    <>
      <Routes location={background ?? location}>
          <Route path="/login" element={<GuestOnly><SignInPage /></GuestOnly>} />
          <Route path="/login/local" element={<RequireCapability name="breakGlass"><GuestOnly><BreakGlassLoginPage /></GuestOnly></RequireCapability>} />
          <Route path="/signup" element={<RequireCapability name="publicSignup"><GuestOnly><PersonalSignupPage /></GuestOnly></RequireCapability>} />
          {/* Phase 16a (D-258): the six digits mailed to the address */}
          <Route path="/signup/verify" element={<RequireCapability name="publicSignup"><GuestOnly><PersonalVerifyPage /></GuestOnly></RequireCapability>} />
          <Route path="/signup/check-email" element={<RequireCapability name="publicSignup"><CheckEmailPage /></RequireCapability>} />
          {/* Phase 16a (D-261): the draft legal texts, signed in or not */}
          <Route path="/yasal/:slug" element={<LegalDraftPage />} />
          {/* 16c.1 (D-259): a marketing mail's unsubscribe link, signed in or not */}
          <Route path="/listeden-cik" element={<RequireCapability name="publicSignup"><UnsubscribePage /></RequireCapability>} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<RequireCapability name="passwordLogin"><PersonalForgotPasswordPage /></RequireCapability>} />
          <Route path="/reset-password" element={<RequireCapability name="passwordLogin"><PersonalResetPasswordPage /></RequireCapability>} />
          <Route path="/invite" element={<RequireCapability name="invites"><InvitePage /></RequireCapability>} />
          {/* v0.15e.2 (D-215): the first-run screen is gone; an old link lands on the home page */}
          <Route path="/onboarding" element={<Navigate to="/" replace />} />
          {/* Phase 16c: signed out, the bireysel address is the landing page; the kurumsal one keeps its sign-in */}
          <Route path="/" element={<Protected guest={capabilities.plans ? <LandingPage /> : undefined}><HomePage /></Protected>} />
          {/* Phase 16c: the prices (signed in or not) and the checkout, where the edition has plans */}
          <Route path="/fiyatlar" element={<RequireCapability name="plans"><PricingPage /></RequireCapability>} />
          <Route path="/odeme" element={<RequireCapability name="billing"><RequireAuth><CheckoutPage /></RequireAuth></RequireCapability>} />
          <Route path="/workspaces" element={<Protected><WorkspacesPage /></Protected>} />
          <Route path="/w/:workspaceId" element={<Protected><WorkspacePage /></Protected>} />
          <Route path="/c/:conversationId" element={<Protected><ConversationPage /></Protected>} />
          <Route path="/memory" element={<Navigate to="/settings/memory" replace />} />
          <Route path="/history" element={<Protected><HistoryPage /></Protected>} />
          <Route path="/skills" element={<Protected><SkillsPage /></Protected>} />
          <Route path="/skills/new" element={<Protected><SkillEditorPage /></Protected>} />
          <Route path="/skills/:skillKey/edit" element={<Protected><SkillEditorPage /></Protected>} />
          <Route path="/connectors" element={<Protected><ConnectorsPage /></Protected>} />
          <Route path="/documents" element={<Protected><DocumentsPage /></Protected>} />
          <Route path="/team" element={<Navigate to="/settings/organization" replace />} />
          <Route path="/admin" element={<RequireCapability name="organization"><Protected><AdminHome /></Protected></RequireCapability>} />
          <Route path="/admin/access" element={<RequireCapability name="organization"><Protected><AccessPage /></Protected></RequireCapability>} />
          <Route path="/admin/access/:tab" element={<RequireCapability name="organization"><Protected><AccessPage /></Protected></RequireCapability>} />
          <Route path="/admin/models" element={<RequireCapability name="organization"><Protected><ModelsAdminPage /></Protected></RequireCapability>} />
          <Route path="/admin/models/:tab" element={<RequireCapability name="organization"><Protected><ModelsAdminPage /></Protected></RequireCapability>} />
          <Route path="/admin/integrations" element={<RequireCapability name="organization"><Protected><IntegrationsPage /></Protected></RequireCapability>} />
          {/* Phase 15e: Microsoft 365's summary and setup; both refuse team admins themselves */}
          <Route path="/admin/integrations/microsoft-365" element={<RequireCapability name="organization"><Protected><Microsoft365Page /></Protected></RequireCapability>} />
          <Route path="/admin/integrations/microsoft-365/setup" element={<RequireCapability name="organization"><Protected><Microsoft365SetupPage /></Protected></RequireCapability>} />
          <Route path="/admin/usage" element={<RequireCapability name="organization"><Protected><UsagePage /></Protected></RequireCapability>} />
          <Route path="/admin/usage/:tab" element={<RequireCapability name="organization"><Protected><UsagePage /></Protected></RequireCapability>} />
          <Route path="/admin/policies" element={<RequireCapability name="organization"><Protected><PoliciesPage /></Protected></RequireCapability>} />
          <Route path="/admin/skills" element={<RequireCapability name="organization"><Protected><SkillAnalyticsPage /></Protected></RequireCapability>} />
          <Route path="/admin/audit" element={<RequireCapability name="organization"><Protected><AuditPage /></Protected></RequireCapability>} />
          <Route path="/admin/identity" element={<RequireCapability name="organization"><Protected><IdentityPage /></Protected></RequireCapability>} />
          <Route path="/admin/license" element={<RequireCapability name="organization"><Protected><LicensePage /></Protected></RequireCapability>} />
          {/* D-135: bireysel only; in kurumsal it is not a page */}
          {capabilities.singleCompany ? null : <Route path="/platform" element={<Protected><PlatformPage /></Protected>} />}
          <Route path="*" element={<Protected><NotFoundPage /></Protected>} />
        </Routes>
      {settings && me.data ? <SettingsDialog onClose={closeSettings} /> : null}
      {/* Phase 16c: the cookie note on the sign-in pages too (the public pages and the legal texts draw their own) */}
      {!me.data && AUTH_PAGES.has(location.pathname) ? <CookieNotice /> : null}
    </>
  );
}

const AUTH_PAGES = new Set(['/login', '/signup', '/signup/verify', '/forgot-password', '/reset-password']);

/** v0.15a.3: a page that fails to draw shows "Bir sorun oldu" instead of a white page; another page tries again. */
function PageBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary name="page" resetKey={location.pathname} fallback={() => <AppError />}>
      {children}
    </ErrorBoundary>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <LocaleSync />
        <PageBoundary>
          <AppRoutes />
        </PageBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

